import { useState, useEffect } from 'react';

import { useRoutePath } from '~/hooks/useRoutePath';
import type { OrderItem, ProductVariantDetails, ProductData, OptionValue } from '~/types/Order';
import { enrichOrderItem } from '~/utils/enrichOrderItem';
import { filterImagesByVariant, getProductImages } from '~/utils/images';

export interface OrderItemDataClient {
  variant: ProductVariantDetails | null;
  product: ProductData | null;
  optionValues: OptionValue[];
  productUrl: string;
  image: string;
  isLoading: boolean;
}

const enrichedItemCache = new Map<string, OrderItem>();

export function useOrderItemDataClient(item: OrderItem): OrderItemDataClient {
  const routePath = useRoutePath();
  const [enrichedItem, setEnrichedItem] = useState<OrderItem>(item);
  const [isLoading, setIsLoading] = useState(typeof item.variant === 'string');

  useEffect(() => {
    if (typeof item.variant === 'object') {
      setEnrichedItem(item);
      setIsLoading(false);
      return;
    }

    const cacheKey = typeof item.variant === 'string' ? item.variant : '';

    if (cacheKey && enrichedItemCache.has(cacheKey)) {
      setEnrichedItem(enrichedItemCache.get(cacheKey)!);
      setIsLoading(false);
      return;
    }

    const doEnrich = async () => {
      const API_URL = window?.ENV?.API_URL || '';
      const enriched = await enrichOrderItem(item, API_URL);

      if (cacheKey) {
        enrichedItemCache.set(cacheKey, enriched);
      }

      setEnrichedItem(enriched);
      setIsLoading(false);
    };

    doEnrich();
  }, [item]);

  const variant = typeof enrichedItem.variant === 'object' ? (enrichedItem.variant as ProductVariantDetails) : null;
  const product = (variant?.product as ProductData) ?? null;
  const optionValues = variant?.optionValues ?? [];
  const productUrlPath = product?.code ? routePath('product', product.slug) : '#';

  // Filter images by variant code, then get the first image
  const filteredImages = filterImagesByVariant(product?.images, variant?.code);
  const images = getProductImages(filteredImages, 'small_thumbnail');
  const image = images[0]?.path ?? '';

  return {
    variant,
    product,
    optionValues,
    productUrl: productUrlPath,
    image,
    isLoading,
  };
}
